import { useEffect, useRef, useState } from "react";
import { ArrowUp, ArrowUpRight, Download, Headphones, KeyRound, LayoutDashboard, Loader2, Lock, Minus, Sparkles, Ticket, X } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import { authHeader } from "@/lib/token";
import { toast } from "sonner";
import AuthModal from "@/components/AuthModal";
import SecretKey from "@/components/SecretKey";

// Durée de l'animation de fermeture (doit suivre .ai-panel[data-state="closing"] dans App.css).
const CLOSE_MS = 260;

const SUGGESTIONS = [
  { icon: LayoutDashboard, text: "Ouvre le tableau de bord de mon bot" },
  { icon: KeyRound, text: "C'est quoi ma clé de licence ?" },
  { icon: Download, text: "Je veux télécharger mon script" },
  { icon: Headphones, text: "Je veux parler à un humain" },
];

const PRIORITY = {
  urgent: { label: "Urgent", cls: "bg-red-500/15 text-red-300" },
  moyen: { label: "Moyen", cls: "bg-amber-500/15 text-amber-300" },
  faible: { label: "Faible", cls: "bg-sky-500/15 text-sky-300" },
};

// Clé d'un produit : celle envoyée avec la réponse, sinon (historique, où la clé
// n'est pas gardée) relue dans « mes produits ».
function LicenseCard({ action }) {
  const [key, setKey] = useState(action.license_key || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (key) return;
    axios.get(`${API}/bots`, { headers: authHeader() })
      .then((r) => setKey((r.data || []).find((b) => b.id === action.bot_id)?.license_key || ""))
      .catch(() => {});
  }, [key, action.bot_id]);
  const regenerate = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/bots/${action.bot_id}/license/regenerate`, {}, { headers: authHeader() });
      setKey(r.data.license_key);
      toast.success("Nouvelle clé générée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de régénérer la clé");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-xl border border-white/[0.08] bg-black/25 p-3" data-testid="ai-license-card">
      <p className="mb-2 flex items-center gap-1.5 text-[12px] font-medium text-white/70"><KeyRound className="h-3.5 w-3.5 text-amber-300" />Clé de licence · {action.name}</p>
      {key ? <SecretKey value={key} compact testid="ai-license" regenerating={busy} onRegenerate={action.active === false ? undefined : regenerate} /> : <p className="text-xs text-white/40">Chargement…</p>}
    </div>
  );
}

function DownloadCard({ action }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/bots/${action.bot_id}/download`, { headers: authHeader(), responseType: "blob" });
      if (res.data && res.data.type === "application/json") {
        const data = JSON.parse(await res.data.text());
        if (data.external_url) { window.open(data.external_url, "_blank", "noopener"); return; }
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = action.filename || "produit.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement impossible");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={download} disabled={busy} data-testid="ai-download" className="flex w-full items-center gap-3 rounded-xl border border-white/[0.08] bg-black/25 p-3 text-left transition hover:border-blue-400/30 hover:bg-blue-500/[0.06] disabled:opacity-60">
      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-500/15 text-blue-300">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-medium text-white">Télécharger {action.name}</span>
        <span className="block truncate text-[11px] text-white/40">{action.filename || "Fichier du produit"}</span>
      </span>
    </button>
  );
}

function ActionCards({ actions, onNavigate }) {
  if (!actions?.length) return null;
  return (
    <div className="mt-2 space-y-2">
      {actions.map((action, index) => {
        if (action.type === "navigate") {
          return (
            <button key={index} onClick={() => onNavigate(action.path)} data-testid="ai-nav-chip" className="inline-flex max-w-full items-center gap-1.5 rounded-lg bg-blue-500/15 px-2.5 py-1.5 text-[12px] font-medium text-blue-200 transition hover:bg-blue-500/25">
              <ArrowUpRight className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{action.label}</span>
            </button>
          );
        }
        if (action.type === "show_license") return <LicenseCard key={index} action={action} />;
        if (action.type === "download") return <DownloadCard key={index} action={action} />;
        if (action.type === "ticket") {
          const prio = PRIORITY[action.priority] || PRIORITY.moyen;
          return (
            <div key={index} className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.06] p-3" data-testid="ai-ticket-card">
              <p className="flex items-center gap-1.5 text-[12.5px] font-semibold text-emerald-200"><Ticket className="h-3.5 w-3.5" />Transmis à l'équipe</p>
              <p className="mt-1 text-[12.5px] text-white/75">« {action.subject} »</p>
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${prio.cls}`}>{prio.label} · {action.category_label}</span>
                <button onClick={() => onNavigate("/support")} className="text-[11.5px] font-medium text-emerald-200 hover:text-white">Voir mon ticket →</button>
              </div>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

function TypingMessage({ text, animate }) {
  const [visibleText, setVisibleText] = useState(animate ? "" : text);

  useEffect(() => {
    if (!animate) {
      setVisibleText(text);
      return undefined;
    }
    let position = 0;
    setVisibleText("");
    const timer = window.setInterval(() => {
      position += 2;
      setVisibleText(text.slice(0, position));
      if (position >= text.length) window.clearInterval(timer);
    }, 18);
    return () => window.clearInterval(timer);
  }, [text, animate]);

  return <>{visibleText}</>;
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1 py-1" aria-label="L'IA écrit">
      {[0, 1, 2].map((index) => (
        <span key={index} className="ai-dot h-1.5 w-1.5 rounded-full bg-blue-300" style={{ animationDelay: `${index * 150}ms` }} />
      ))}
    </div>
  );
}

function AiAvatar({ size = "h-7 w-7" }) {
  return (
    <span className={`relative flex ${size} shrink-0 rounded-full bg-gradient-to-br from-blue-500 to-violet-600 p-[1.5px]`}>
      <img src="/images/logo-navbar.png" alt="" className="h-full w-full rounded-full bg-[#0b1020] object-cover" />
    </span>
  );
}

export default function SupportAIWidget() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [hint, setHint] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  // Le panneau reste monté pendant l'animation de fermeture.
  useEffect(() => {
    if (open) { setMounted(true); return undefined; }
    const id = window.setTimeout(() => setMounted(false), CLOSE_MS);
    return () => window.clearTimeout(id);
  }, [open]);

  // Petite bulle « Besoin d'aide ? » une fois par session, quelques secondes après l'arrivée.
  useEffect(() => {
    let seen = false;
    try { seen = sessionStorage.getItem("vp_ai_hint") === "1"; } catch { /* ignore */ }
    if (seen) return undefined;
    const show = window.setTimeout(() => setHint(true), 6000);
    const hide = window.setTimeout(() => setHint(false), 14000);
    return () => { window.clearTimeout(show); window.clearTimeout(hide); };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    setHint(false);
    try { sessionStorage.setItem("vp_ai_hint", "1"); } catch { /* ignore */ }
    const onKey = (event) => { if (event.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    const id = window.setTimeout(() => inputRef.current?.focus(), 320);
    return () => { window.removeEventListener("keydown", onKey); window.clearTimeout(id); };
  }, [open]);

  useEffect(() => {
    if (!open || !user || chat) return;
    const loadChat = async () => {
      try {
        const response = await axios.get(`${API}/support-ai/chats`, { headers: authHeader() });
        const existing = response.data?.[0];
        if (existing) {
          setChat(existing);
          const messagesResponse = await axios.get(`${API}/support-ai/chats/${existing.id}/messages`, { headers: authHeader() });
          setMessages(messagesResponse.data || []);
          return;
        }
        const created = await axios.post(`${API}/support-ai/chats`, { title: "Support VantaPulse AI" }, { headers: authHeader() });
        setChat(created.data);
      } catch {
        toast.error("Le support IA est indisponible");
      }
    };
    loadChat();
  }, [open, user, chat]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Hauteur de la zone de saisie qui suit le texte (jusqu'à 5 lignes environ).
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [input]);

  const sendMessage = async (preset) => {
    const text = (preset ?? input).trim();
    if (!text || !chat || loading) return;
    setInput("");
    setMessages((current) => [...current, { id: `local-${Date.now()}`, role: "user", text }]);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/support-ai/chats/${chat.id}/message`, { text }, { headers: authHeader() });
      const actions = response.data.actions || [];
      setMessages((current) => [...current, { id: response.data.message_id, role: "assistant", text: response.data.text, actions, animate: true }]);
      // L'assistant ouvre la page tout seul, juste après sa réponse.
      const go = actions.find((a) => a.type === "navigate");
      if (go) window.setTimeout(() => { navigate(go.path); if (window.innerWidth < 640) setOpen(false); }, 700);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible de contacter l'IA");
    } finally {
      setLoading(false);
    }
  };

  const closeTicket = async () => {
    if (!chat || loading) return;
    try {
      await axios.put(`${API}/support-ai/chats/${chat.id}/close`, {}, { headers: authHeader() });
      setChat(null);
      setMessages([]);
      setInput("");
      toast.success("Ticket fermé");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible de fermer le ticket");
    }
  };

  const firstName = (user?.username || "").split(" ")[0];

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-4" data-testid="support-ai-widget">
      {mounted && (
        <div
          data-state={open ? "open" : "closing"}
          role="dialog"
          aria-label="Support VantaPulse AI"
          className="ai-panel relative flex h-[min(620px,calc(100vh-7.5rem))] w-[min(400px,calc(100vw-2.5rem))] flex-col overflow-hidden rounded-[22px] border border-white/[0.08] bg-[#0a0d18]/[0.985] shadow-[0_30px_80px_-20px_rgba(37,99,235,0.45),0_0_0_1px_rgba(255,255,255,0.02)] backdrop-blur-2xl"
        >
          {/* Halo de couleur en haut du panneau */}
          <div className="pointer-events-none absolute -top-24 left-1/2 h-48 w-[140%] -translate-x-1/2 rounded-full bg-gradient-to-r from-blue-600/30 via-violet-600/25 to-cyan-500/20 blur-3xl" />

          <div className="relative flex items-center justify-between px-4 pb-3 pt-4">
            <div className="flex min-w-0 items-center gap-3">
              <span className="relative flex">
                <AiAvatar size="h-10 w-10" />
                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[#0a0d18] bg-emerald-400" />
              </span>
              <div className="leading-tight">
                <p className="flex items-center gap-1.5 text-[15px] font-semibold text-white">
                  VantaPulse AI
                  <span className="rounded-md bg-gradient-to-r from-blue-500/25 to-violet-500/25 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-blue-200 ring-1 ring-inset ring-white/10">Support</span>
                </p>
                <p className="mt-0.5 text-[11.5px] text-white/45">En ligne · réponse instantanée</p>
              </div>
            </div>
            <div className="flex items-center gap-0.5">
              {user && chat && (
                <button onClick={closeTicket} disabled={loading} className="whitespace-nowrap rounded-lg px-2 py-1.5 text-[11px] font-medium text-white/45 transition hover:bg-white/[0.06] hover:text-red-300 disabled:opacity-30" data-testid="support-ai-close-ticket">
                  Fermer le ticket
                </button>
              )}
              <button onClick={() => setOpen(false)} className="rounded-lg p-2 text-white/45 transition hover:bg-white/[0.06] hover:text-white" aria-label="Réduire le support IA">
                <Minus className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="relative mx-4 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

          <div className="support-ai-scrollbar relative flex-1 space-y-4 overflow-y-auto px-4 py-5">
            {!user && (
              <div className="ai-msg flex h-full flex-col items-center justify-center text-center">
                <span className="ai-orb relative mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-violet-600 shadow-lg shadow-blue-900/40">
                  <Lock className="h-7 w-7 text-white" />
                </span>
                <p className="text-base font-semibold text-white">Connecte-toi pour discuter</p>
                <p className="mt-1.5 max-w-[260px] text-[13px] leading-relaxed text-white/45">L'assistant retrouve tes produits et ton historique pour t'aider plus vite.</p>
                <button onClick={() => setShowAuth(true)} className="mt-5 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 px-5 py-2.5 text-sm font-medium text-white shadow-lg shadow-blue-900/30 transition hover:brightness-110 active:scale-[0.98]">
                  Se connecter
                </button>
              </div>
            )}

            {user && messages.length === 0 && (
              <div className="ai-msg flex flex-col items-center pt-4 text-center">
                <span className="ai-orb relative mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-violet-600 shadow-lg shadow-blue-900/40">
                  <Sparkles className="h-7 w-7 text-white" />
                </span>
                <p className="text-lg font-semibold text-white">Bonjour{firstName ? ` ${firstName}` : ""}</p>
                <p className="mt-1 text-[13px] text-white/45">Comment puis-je t'aider aujourd'hui ?</p>
                <div className="mt-6 grid w-full gap-2">
                  {SUGGESTIONS.map(({ icon: Icon, text }, index) => (
                    <button
                      key={text}
                      onClick={() => sendMessage(text)}
                      disabled={!chat || loading}
                      className="ai-msg group flex items-center gap-3 rounded-xl border border-white/[0.07] bg-white/[0.03] px-3.5 py-2.5 text-left text-[13px] text-white/75 transition hover:border-blue-400/30 hover:bg-blue-500/[0.08] hover:text-white disabled:opacity-40"
                      style={{ animationDelay: `${120 + index * 60}ms` }}
                    >
                      <Icon className="h-4 w-4 shrink-0 text-blue-300/80 transition group-hover:text-blue-300" />
                      {text}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((message) => (
              message.role === "user" ? (
                <div key={message.id} className="ai-msg flex justify-end">
                  <div className="max-w-[82%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-gradient-to-br from-blue-600 to-indigo-600 px-3.5 py-2.5 text-[13.5px] leading-relaxed text-white shadow-md shadow-blue-950/40">
                    {message.text}
                  </div>
                </div>
              ) : (
                <div key={message.id} className="ai-msg flex items-start gap-2" data-testid="ai-msg-assistant">
                  <AiAvatar />
                  <div className="min-w-0 max-w-[82%]">
                    <div className="whitespace-pre-wrap rounded-2xl rounded-bl-md border border-white/[0.06] bg-white/[0.05] px-3.5 py-2.5 text-[13.5px] leading-relaxed text-white/85">
                      <TypingMessage text={message.text} animate={message.animate} />
                    </div>
                    <ActionCards actions={message.actions} onNavigate={navigate} />
                  </div>
                </div>
              )
            ))}
            {loading && (
              <div className="ai-msg flex items-end gap-2">
                <AiAvatar />
                <div className="rounded-2xl rounded-bl-md border border-white/[0.06] bg-white/[0.05] px-3.5 py-2"><TypingDots /></div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {user && (
            <div className="relative px-3 pb-3 pt-1">
              <div className="flex items-end gap-2 rounded-2xl border border-white/[0.08] bg-white/[0.04] py-1.5 pl-3.5 pr-1.5 transition focus-within:border-blue-400/40 focus-within:bg-white/[0.06] focus-within:shadow-[0_0_0_4px_rgba(59,130,246,0.08)]">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); sendMessage(); } }}
                  placeholder="Écris ton message…"
                  rows={1}
                  className="max-h-[120px] flex-1 resize-none bg-transparent py-1.5 text-[13.5px] text-white outline-none placeholder:text-white/30"
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || loading || !chat}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 text-white shadow-md shadow-blue-900/40 transition hover:brightness-110 active:scale-95 disabled:from-white/10 disabled:to-white/10 disabled:text-white/30 disabled:shadow-none"
                  aria-label="Envoyer le message"
                >
                  <ArrowUp className="h-4 w-4" strokeWidth={2.4} />
                </button>
              </div>
              <p className="mt-2 text-center text-[10.5px] text-white/25">L'IA peut se tromper · un humain prend le relais si besoin</p>
            </div>
          )}
        </div>
      )}
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} mode="login" setMode={() => {}} />

      <div className="relative">
        {hint && !open && (
          <button onClick={() => setOpen(true)} className="ai-hint absolute bottom-2 right-[4.75rem] whitespace-nowrap rounded-2xl rounded-br-md border border-white/10 bg-[#111527]/95 px-3.5 py-2 text-[13px] text-white/85 shadow-xl backdrop-blur-xl">
            Besoin d'aide ? <span className="text-blue-300">Demande à l'IA</span>
          </button>
        )}
        <button
          onClick={() => setOpen((current) => !current)}
          className={`ai-launcher group relative flex h-[60px] w-[60px] items-center justify-center rounded-full text-white transition-transform duration-300 ease-out hover:scale-[1.06] active:scale-95 ${open ? "is-open" : ""}`}
          aria-label={open ? "Fermer Support VantaPulse AI" : "Ouvrir Support VantaPulse AI"}
          aria-expanded={open}
          data-testid="support-ai-toggle"
        >
          <span className="ai-launcher-ring absolute inset-0 rounded-full" />
          <span className="absolute inset-[2px] rounded-full bg-gradient-to-br from-blue-500 via-indigo-600 to-violet-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_10px_30px_-6px_rgba(79,70,229,0.7)]" />
          {!open && <span className="ai-launcher-pulse absolute inset-0 rounded-full" />}
          <Sparkles className={`absolute h-6 w-6 transition-all duration-300 ${open ? "rotate-90 scale-50 opacity-0" : "rotate-0 scale-100 opacity-100"}`} strokeWidth={2} />
          <X className={`absolute h-6 w-6 transition-all duration-300 ${open ? "rotate-0 scale-100 opacity-100" : "-rotate-90 scale-50 opacity-0"}`} strokeWidth={2.2} />
          {!open && <span className="absolute right-0.5 top-0.5 h-3.5 w-3.5 rounded-full border-[2.5px] border-[#0a0a0f] bg-emerald-400" />}
        </button>
      </div>
    </div>
  );
}
